import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../api/client';
import { communications, parties, requests } from '../api/endpoints';
import type {
    AutoLinkedItem, ChannelAccount, ClientAccount, InboxDecision,
    InboxItem, RequestEntity
} from '../api/types';
import { FilePreviewModal } from '../components/FilePreviewModal';
import {
    Card, Column, DataTable,
    ErrorBox, Field, fmtDate, label, PageHeader, shortId, StatusBadge
} from '../components/ui';
import { navigate, useAsync } from '../hooks';

type DecisionMode = '' | 'new' | 'existing' | 'not_request';

const DECISION_LABELS: Record<string, string> = {
  new: 'Новая заявка',
  existing: 'В существующую',
  not_request: 'Не заявка',
};

// --- Нормализация элемента очереди: backend может вернуть «плоское»
//     сообщение или обёртку { message: {...} } — читаем оба варианта. ---

function innerMessage(it: InboxItem) {
  return it.message && typeof it.message === 'object' ? it.message : null;
}

export function inboxMessageId(it: InboxItem): string {
  if (it.message_id) return it.message_id;
  if (typeof it.message === 'string') return it.message;
  const inner = innerMessage(it);
  if (inner?.id) return inner.id;
  return it.id;
}

function inboxSender(it: InboxItem): string {
  return it.sender ?? innerMessage(it)?.sender ?? it.peer_address ?? '—';
}

function inboxSubject(it: InboxItem): string {
  return it.subject ?? innerMessage(it)?.subject ?? '';
}

function inboxSnippet(it: InboxItem): string {
  const body = it.snippet ?? it.body ?? innerMessage(it)?.body ?? '';
  return body.length > 160 ? `${body.slice(0, 160)}…` : body;
}

function inboxTime(it: InboxItem): string {
  return fmtDate(it.sent_at ?? innerMessage(it)?.sent_at ?? it.received_at ?? it.created_at);
}

function inboxAttachCount(it: InboxItem): number {
  if (it.attachments_count != null) return it.attachments_count;
  const own = it.attachments ?? innerMessage(it)?.attachments;
  return own?.length ?? 0;
}

function channelLabel(it: InboxItem, accounts: Map<string, ChannelAccount>): string {
  // Серверная подпись канала (в т.ч. имя пользовательского ящика) —
  // без админ-доступа к справочнику каналов.
  if (it.channel_label) return it.channel_label;
  const acc = it.channel ? accounts.get(it.channel) : undefined;
  if (acc) {
    const type = acc.type === 'whatsapp' ? 'WhatsApp' : acc.type === 'email' ? 'Email' : acc.type ?? 'Канал';
    return acc.mailbox_or_number ? `${type} · ${acc.mailbox_or_number}` : type;
  }
  const t = it.channel_type ?? it.channel_provider ?? innerMessage(it)?.channel_type ?? it.provider ?? innerMessage(it)?.provider;
  if (t === 'whatsapp' || t === 'whatsapp_cloud') return 'WhatsApp';
  if (t === 'email' || t === 'imap' || t === 'webhook_email') return 'Email';
  return t ?? (it.channel ? shortId(it.channel) : '—');
}

// ---------- Панель решения ----------

function DecidePanel({ mode, selectedIds, onDone, onError, initialRequestId }: {
  mode: DecisionMode;
  selectedIds: string[];
  onDone: (notice: string, requestId?: string) => void;
  onError: (e: unknown) => void;
  initialRequestId?: string;
}) {
  const accounts = useAsync<ClientAccount[]>(
    () => (mode === 'new' ? parties.clientAccounts() : Promise.resolve([])),
    [mode],
  );
  const requestList = useAsync<RequestEntity[]>(
    () => (mode === 'existing' ? requests.list() : Promise.resolve([])),
    [mode],
  );
  const [accountId, setAccountId] = useState('');
  const [requestId, setRequestId] = useState(initialRequestId ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  if (!mode) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await communications.decide({
        message_ids: selectedIds,
        decision: mode,
        client_account_id: accountId || undefined,
        request_id: mode === 'existing' ? requestId : undefined,
        reason: reason || undefined,
      });
      const createdId = res?.request_id ?? res?.results?.find((r) => r.request_id)?.request_id;
      const labelText = res?.request_number ? `заявка ${res.request_number}` : 'заявка';
      onDone(
        mode === 'new' ? `Решение применено — создана ${labelText}.`
          : mode === 'existing' ? 'Решение применено — сообщения привязаны к заявке.'
          : 'Решение применено — сообщения помечены как «не заявка».',
        createdId,
      );
    } catch (x) {
      onError(x);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-form" data-testid="inbox-decide-panel">
      {mode === 'new' && (
        <Field label="Клиентский счёт" hint="Если не выбран — backend попробует определить по контакту отправителя">
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">— определить автоматически —</option>
            {(accounts.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>{a.party_name ?? a.party}</option>
            ))}
          </select>
        </Field>
      )}
      {mode === 'existing' && (
        <Field label="Заявка">
          <select value={requestId} onChange={(e) => setRequestId(e.target.value)} required>
            <option value="">— выберите заявку —</option>
            {(requestList.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>{r.number ?? shortId(r.id)}</option>
            ))}
          </select>
        </Field>
      )}
      {mode === 'not_request' && (
        <Field label="Причина">
          <input value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="Например: спам, информационное письмо" />
        </Field>
      )}
      <div className="form-actions">
        <button className="btn primary" onClick={submit}
                disabled={busy || selectedIds.length === 0 || (mode === 'existing' && !requestId)}>
          {busy ? 'Применение…' : `Применить (${selectedIds.length})`}
        </button>
      </div>
    </div>
  );
}

// ---------- Страница ----------

export function InboxPage() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<DecisionMode>('');
  const [actionErr, setActionErr] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const [noticeRequestId, setNoticeRequestId] = useState<string | undefined>();
  const [prefillRequestId, setPrefillRequestId] = useState('');
  const [previewIdx, setPreviewIdx] = useState<{ items: { blobId: string; name: string }[]; index: number } | null>(null);

  const list = useAsync<InboxItem[]>(() => communications.inbox(), []);
  const accounts = useAsync<ChannelAccount[]>(
    () => communications.channelAccounts().catch(() => [] as ChannelAccount[]),
    [],
  );
  // Письма, автоматически связанные с заявками — куда ушло из очереди.
  const linked = useAsync<AutoLinkedItem[]>(
    () => communications.inboxAutoLinked().catch(() => [] as AutoLinkedItem[]),
    [],
  );
  // История решений доступна не всем ролям — 403 просто скрывает блок.
  const decisions = useAsync<InboxDecision[] | null>(
    () => communications.inboxDecisions().catch((e) => {
      if (e instanceof ApiError && (e.isForbidden || e.isNotFound)) return null;
      throw e;
    }),
    [],
  );

  const accountMap = useMemo(
    () => new Map((accounts.data ?? []).map((a) => [a.id, a])),
    [accounts.data],
  );

  // Автообновление очереди каждые 15 с, пока страница открыта; выбор
  // чекбоксов хранится по id сообщений и при фоновом reload сохраняется.
  useEffect(() => {
    const t = setInterval(() => {
      void list.reload();
      void linked.reload();
    }, 15_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const rows = list.data ?? [];
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(inboxMessageId(r)));

  const toggleAll = () => {
    setSelected(allChecked ? new Set() : new Set(rows.map(inboxMessageId)));
  };

  const onDecided = (text: string, requestId?: string) => {
    setNotice(text);
    setNoticeRequestId(requestId);
    setSelected(new Set());
    setMode('');
    list.reload();
    decisions.reload();
  };

  const columns: Column<InboxItem>[] = [
    {
      key: 'sel', title: '', width: '30px',
      render: (r) => (
        <input type="checkbox" aria-label="Выбрать сообщение"
               checked={selected.has(inboxMessageId(r))}
               onChange={() => toggle(inboxMessageId(r))} />
      ),
    },
    { key: 'channel', title: 'Канал', render: (r) => channelLabel(r, accountMap), width: '150px' },
    { key: 'sender', title: 'Отправитель', render: (r) => inboxSender(r) },
    { key: 'time', title: 'Время', render: (r) => inboxTime(r), width: '140px' },
    {
      key: 'subject', title: 'Тема / текст',
      render: (r) => (
        <div>
          {inboxSubject(r) && <div><strong>{inboxSubject(r)}</strong></div>}
          <div className="muted">{inboxSnippet(r) || '—'}</div>
          {r.routing_note && (
            <div className="alert alert-error" style={{ marginTop: 4, fontSize: 12, padding: '4px 8px' }}>
              {r.routing_note}
            </div>
          )}
          {r.suggested_request_id && (
            <div style={{ marginTop: 4, fontSize: 12 }}>
              <button className="btn small" onClick={() => {
                setSelected(new Set([inboxMessageId(r)]));
                setPrefillRequestId(r.suggested_request_id ?? '');
                setMode('existing');
              }}>
                Привязать к {r.suggested_request_number || 'найденной заявке'} →
              </button>
            </div>
          )}
          {(r.candidate_requests ?? []).length > 0 && (
            <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
              Заявки отправителя: {(r.candidate_requests ?? []).map((c) => (
                <button key={c.id} className="btn small" style={{ marginRight: 4, marginTop: 2 }}
                        onClick={() => {
                          setSelected(new Set([inboxMessageId(r)]));
                          setPrefillRequestId(c.id);
                          setMode('existing');
                        }}>
                  {c.number || c.title}
                </button>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'attachments', title: 'Вложения', width: '110px',
      render: (r) => {
        const atts = (r.attachments ?? [])
          .filter((a) => typeof a === 'object') as Array<{ id: string; file_blob: string; name: string }>;
        if (atts.length > 0) {
          return (
            <div>
              <span className="badge info" title={`Вложений: ${atts.length}`}>{atts.length}</span>
              {atts.map((a, i) => (
                <button key={a.id} className="btn small" style={{ marginRight: 4, marginTop: 2 }}
                        title={a.name}
                        onClick={() => setPreviewIdx({
                          items: atts.map((x) => ({ blobId: x.file_blob, name: x.name })),
                          index: i,
                        })}>
                  📎 {a.name.length > 18 ? `${a.name.slice(0, 18)}…` : a.name}
                </button>
              ))}
            </div>
          );
        }
        const n = inboxAttachCount(r);
        return n > 0 ? <span className="badge info">{n}</span> : <span className="muted">—</span>;
      },
    },
    {
      key: 'status', title: 'Статус', width: '110px',
      render: (r) => <StatusBadge value={r.status ?? r.delivery_status ?? 'pending'} />,
    },
  ];

  return (
    <div>
      <PageHeader title="Входящие">
        <button className="btn" onClick={() => { list.reload(); linked.reload(); decisions.reload(); }}>Обновить</button>
      </PageHeader>
      <div className="muted" style={{ marginBottom: 8, fontSize: 13 }}>
        Здесь показаны необработанные сообщения. Письма, связанные с заявками,
        находятся в переписке соответствующих заявок. Список обновляется автоматически.
      </div>

      {notice && (
        <div className="alert alert-info">
          <span>{notice}</span>
          {noticeRequestId && (
            <a href={`#/requests/${noticeRequestId}`}
               onClick={(e) => { e.preventDefault(); navigate(`/requests/${noticeRequestId}`); }}>
              Открыть заявку →
            </a>
          )}
        </div>
      )}
      <ErrorBox error={actionErr} />
      <ErrorBox error={list.error} onRetry={list.reload} />

      {selected.size > 0 && (
        <Card title={`Решение по выбранным (${selected.size})`}>
          <div className="form-actions">
            <button className={`btn small ${mode === 'new' ? 'primary' : ''}`}
                    onClick={() => setMode(mode === 'new' ? '' : 'new')}>
              Новая заявка
            </button>
            <button className={`btn small ${mode === 'existing' ? 'primary' : ''}`}
                    onClick={() => setMode(mode === 'existing' ? '' : 'existing')}>
              В существующую
            </button>
            <button className={`btn small ${mode === 'not_request' ? 'primary' : ''}`}
                    onClick={() => setMode(mode === 'not_request' ? '' : 'not_request')}>
              Не заявка
            </button>
          </div>
          <DecidePanel key={mode + prefillRequestId} mode={mode} selectedIds={[...selected]}
                       initialRequestId={prefillRequestId}
                       onDone={onDecided} onError={setActionErr} />
        </Card>
      )}

      <Card title="Очередь необработанных сообщений"
            actions={rows.length > 0 ? (
              <label className="muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                Все
              </label>
            ) : undefined}>
        {list.loading && !list.data ? (
          <div className="loading">Загрузка входящих…</div>
        ) : (
          <DataTable columns={columns} rows={rows}
                     empty={list.error
                       ? 'Ошибка загрузки — см. сообщение выше'
                       : 'Очередь пуста — необработанных входящих нет'} />
        )}
      </Card>

      {(linked.data ?? []).length > 0 && (
        <Card title="Направлено в заявки автоматически">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Время</th><th>Канал</th><th>Отправитель</th><th>Тема</th><th>Заявка</th>
                </tr>
              </thead>
              <tbody>
                {(linked.data ?? []).map((m) => (
                  <tr key={m.id}>
                    <td className="nowrap muted">{inboxTime(m)}</td>
                    <td className="muted">{channelLabel(m, accountMap)}</td>
                    <td>{inboxSender(m)}</td>
                    <td>{inboxSubject(m) || '—'}</td>
                    <td>
                      {m.request_id
                        ? <a href={`#/requests/${m.request_id}`}>{m.request_number || shortId(m.request_id)}</a>
                        : '—'}
                      {m.link_reason && (
                        <div className="muted" style={{ fontSize: 12 }}>{m.link_reason}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {decisions.data && decisions.data.length > 0 && (
        <Card title="История решений">
          <ErrorBox error={decisions.error} />
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Время</th><th>Решение</th><th>Сообщение</th><th>Заявка</th><th>Причина</th>
                </tr>
              </thead>
              <tbody>
                {decisions.data.map((d) => (
                  <tr key={d.id}>
                    <td className="nowrap">{fmtDate(d.created_at)}</td>
                    <td>{DECISION_LABELS[d.decision ?? ''] ?? label(d.decision)}</td>
                    <td className="muted">{shortId(d.message ?? d.conversation)}</td>
                    <td>
                      {d.request
                        ? <a href={`#/requests/${d.request}`}>{shortId(d.request)}</a>
                        : '—'}
                    </td>
                    <td>{d.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {previewIdx && (
        <FilePreviewModal items={previewIdx.items} index={previewIdx.index}
                          onClose={() => setPreviewIdx(null)}
                          onNavigate={(i) => setPreviewIdx((p) => p && { ...p, index: i })} />
      )}
    </div>
  );
}
